import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { session } from "@/session";

// Route-level code splitting. Only the shell is in the initial chunk.
const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: { title: "Sign in", public: true, layout: "auth" },
  },
  {
    path: "/register",
    name: "register",
    component: () => import("@/views/RegisterView.vue"),
    meta: { title: "Create an account", public: true, layout: "auth" },
  },
  {
    path: "/",
    name: "dashboard",
    component: () => import("@/views/DashboardView.vue"),
    meta: { title: "Dashboard" },
  },
  {
    path: "/servers",
    name: "servers",
    component: () => import("@/views/ServersView.vue"),
    meta: { title: "Servers" },
  },
  // Drill-downs, reached from the list above rather than from the sidebar, so
  // neither adds a nav entry. The title stays generic because the topbar renders
  // it before the entity is loaded and "Server" is honest where a stale name
  // would not be.
  {
    path: "/servers/:serverId",
    name: "server",
    component: () => import("@/views/ServerDetailView.vue"),
    meta: { title: "Server" },
  },
  {
    path: "/servers/:serverId/settings",
    name: "server-settings",
    component: () => import("@/views/ServerSettingsView.vue"),
    meta: { title: "Server settings" },
  },
  // Its own route rather than a panel on the server page: an operator running
  // commands wants the scrollback to survive, and a console sharing a page with
  // a 15-second poll would be rebuilt underneath them.
  {
    path: "/servers/:serverId/console",
    name: "server-console",
    component: () => import("@/views/RconConsoleView.vue"),
    meta: { title: "Console" },
  },
  // Machines the customer owns, rather than servers we reach. Its own page and
  // not a step inside the add-server wizard, because approving an enrolment is
  // something an owner comes back to later, after the person who started it has
  // walked away from their browser.
  {
    path: "/agents",
    name: "agents",
    component: () => import("@/views/AgentsView.vue"),
    meta: { title: "Agents" },
  },
  {
    path: "/players",
    name: "players",
    component: () => import("@/views/PlayersView.vue"),
    meta: { title: "Players" },
  },
  {
    path: "/players/:steam64",
    name: "player",
    component: () => import("@/views/PlayerDetailView.vue"),
    meta: { title: "Player" },
  },
  {
    path: "/bans",
    name: "bans",
    component: () => import("@/views/BansView.vue"),
    meta: { title: "Bans" },
  },
  {
    path: "/plan",
    name: "plan",
    component: () => import("@/views/PlanView.vue"),
    meta: { title: "Plan" },
  },
  // Public files behind an authenticated page. The archive itself is served
  // unauthenticated by Caddy, because the mod has to be installed before an
  // account is worth anything.
  {
    path: "/downloads",
    name: "downloads",
    component: () => import("@/views/DownloadsView.vue"),
    meta: { title: "Downloads" },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "notfound",
    component: () => import("@/views/NotFoundView.vue"),
    meta: { title: "Not found" },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * Everything is private unless a route opts out with `public: true`.
 *
 * Default-deny rather than tagging protected routes individually: forgetting to
 * mark a new route then leaks it, whereas forgetting to mark one public merely
 * makes it require a login, which is obvious the first time anyone tries.
 *
 * This is cosmetic. It stops a signed-out browser rendering a shell it has no
 * data for; it is not a security control. Every endpoint re-checks server-side,
 * because a route guard is trivially bypassed from the console.
 */
router.beforeEach(async (to) => {
  if (!session.state.ready) {
    await session.load();
  }

  const isPublic = to.meta.public === true;

  if (!isPublic && !session.state.authenticated) {
    return { name: "login", query: { returnTo: to.fullPath } };
  }

  // A signed-in user has no use for the login screen.
  if (isPublic && session.state.authenticated) {
    return { name: "dashboard" };
  }

  return true;
});

router.afterEach((to) => {
  const title = to.meta.title as string | undefined;
  document.title = title ? `${title} · SUDO` : "SUDO Game Servers";
});
